import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '@/components/ui/button';
import {
  fetchFeaturedStudioRecipes,
  FeaturedRecipe,
} from '@/lib/featuredRecipesService';
import { chefPersonas } from '@/config/chefPersonas';
import {
  Sparkles,
  FileText,
  Activity,
  ArrowRight,
  ChefHat,
  Star,
  Clock,
} from 'lucide-react';
import ChefCoinIcon from '@/components/ui/ChefCoinIcon';
import { useNavigate } from 'react-router-dom';

const PILLARS = [
  {
    icon: Sparkles,
    accent: 'text-terracotta-500',
    bg: 'bg-terracotta-500/10',
    title: 'Create & Visualize',
    description: 'Collaborate with AI chefs & generate stunning recipes.',
  },
  {
    icon: FileText,
    accent: 'text-sky-600',
    bg: 'bg-sky-500/10',
    title: 'Import & Organize',
    description: 'Copy, clean-up & standardise recipes from any website instantly.',
  },
  {
    icon: Activity,
    accent: 'text-emerald-600',
    bg: 'bg-emerald-500/10',
    title: 'Analyze & Adapt',
    description: 'Real-time nutritional insights & dietary adjustments.',
  },
];

// Stable, publicly-known Unsplash photo IDs (food images)
const MOSAIC_CARDS = [
  {
    id: 'feature',
    span: 'col-span-2 row-span-2',
    image: '/Cheflab_welcome.jpeg',
    title: 'Meet the Chefs',
    meta: 'Six personas, one kitchen',
    tag: 'Studio',
    minutes: null as number | null,
    rating: null as number | null,
    rotate: '-rotate-1',
    translate: '',
  },
  {
    id: 'a',
    span: 'col-span-1 row-span-1',
    image: '',
    title: '',
    meta: '',
    tag: 'Studio',
    minutes: null as number | null,
    rating: null as number | null,
    rotate: 'rotate-2',
    translate: 'sm:translate-y-6',
  },
  {
    id: 'b',
    span: 'col-span-1 row-span-1',
    image: '',
    title: '',
    meta: '',
    tag: 'Studio',
    minutes: null as number | null,
    rating: null as number | null,
    rotate: '-rotate-2',
    translate: '',
  },
  {
    id: 'c',
    span: 'col-span-1 row-span-1',
    image: '',
    title: '',
    meta: '',
    tag: 'Studio',
    minutes: null as number | null,
    rating: null as number | null,
    rotate: 'rotate-1',
    translate: 'sm:translate-y-4',
  },
  {
    id: 'd',
    span: 'col-span-1 row-span-1',
    image: '',
    title: '',
    meta: '',
    tag: 'Studio',
    minutes: null as number | null,
    rating: null as number | null,
    rotate: '-rotate-2',
    translate: '',
  },
  {
    id: 'e',
    span: 'col-span-1 row-span-1',
    image: '',
    title: '',
    meta: '',
    tag: 'Studio',
    minutes: null as number | null,
    rating: null as number | null,
    rotate: 'rotate-1',
    translate: '-translate-y-2',
  },
];

export const LoginPage: React.FC = () => {
  const { user, signInWithGoogle, isLoading } = useAuth();
  const navigate = useNavigate();
  const [imgErrors, setImgErrors] = useState<Record<string, boolean>>({});
  const [featured, setFeatured] = useState<FeaturedRecipe[]>([]);

  useEffect(() => {
    let cancelled = false;
    fetchFeaturedStudioRecipes(5).then((rows) => {
      if (!cancelled) setFeatured(rows);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // Merge fetched recipes into the 5 SMALL slots only (idx 1-5).
  // The feature card (idx 0) keeps its original "Meet the Chefs" hero image.
  const liveCards = MOSAIC_CARDS.map((slot, idx) => {
    if (idx === 0) return slot; // feature card — never overwrite
    const r = featured[idx - 1];
    if (!r) return slot;

    // Resolve the chef persona from the recipe's tags column.
    // Mirrors the logic in ModernRecipeCard.tsx: find the first tag that
    // matches a known persona id.
    const chefId = r.tags?.find((t) => chefPersonas.some((p) => p.id === t));
    const chef = chefId
      ? chefPersonas.find((p) => p.id === chefId)
      : undefined;

    return {
      ...slot,
      image: r.image_url,
      title: r.title,
      meta: chef ? chef.name : 'Studio',
      recipeId: r.id,
      chefAvatar: chef?.avatar,
      chefName: chef?.name,
    } as typeof slot & {
      recipeId?: number;
      chefAvatar?: string;
      chefName?: string;
    };
  });

  const handleImgError = (id: string) =>
    setImgErrors((s) => ({ ...s, [id]: true }));

  return (
    <div className="min-h-screen w-full bg-[#FAF7F2] dark:bg-[#1A1714] text-foreground overflow-x-hidden">
      <div className="flex flex-col md:flex-row min-h-screen">
        {/* ============== LEFT: brand + CTA + pillars ============== */}
        <div className="order-2 md:order-1 w-full md:w-5/12 lg:w-2/5 flex flex-col px-5 sm:px-10 lg:px-14 py-8 md:py-12 lg:py-16 relative">
          {/* Logo */}
          <div className="mb-6 md:mb-12">
            <img
              src="/Cheflab.png"
              alt="Cheflab"
              className="h-9 sm:h-11 w-auto"
            />
          </div>

          {/* Headline */}
          <div className="space-y-3 md:space-y-4 mb-6">
            <h1 className="text-[2.1rem] leading-[1.08] sm:text-5xl lg:text-6xl font-bold tracking-tight">
              The Culinary{' '}
              <span className="relative inline-block">
                <span className="text-transparent bg-clip-text bg-gradient-to-br from-terracotta-500 via-orange-500 to-amber-500">
                  Studio
                </span>
                <svg
                  viewBox="0 0 200 12"
                  className="absolute -bottom-1 left-0 w-full h-2 text-terracotta-400/70"
                  preserveAspectRatio="none"
                  aria-hidden
                >
                  <path
                    d="M2 8 Q 50 1, 100 6 T 198 5"
                    stroke="currentColor"
                    strokeWidth="3"
                    fill="none"
                    strokeLinecap="round"
                  />
                </svg>
              </span>
            </h1>

            <p className="text-base sm:text-lg text-muted-foreground max-w-md leading-relaxed">
              Your AI-powered creative workspace for generating, organizing and
              analysing recipes — guided by six distinct chef personas.
            </p>
          </div>

          {/* CTA */}
          <div className="space-y-3 md:space-y-4 mb-8 md:mb-10">
            {user ? (
              <Button
                onClick={() => navigate('/')}
                size="lg"
                className="w-full sm:w-auto min-w-[260px] h-13 sm:h-14 text-base sm:text-lg font-semibold bg-terracotta-500 hover:bg-terracotta-600 text-white shadow-lg shadow-terracotta-500/25 hover:shadow-xl hover:shadow-terracotta-500/30 hover:-translate-y-0.5 active:translate-y-0 transition-all duration-300 rounded-full px-7 py-3"
              >
                <span className="flex items-center gap-3">
                  Enter Studio
                  <ArrowRight className="h-5 w-5" />
                </span>
              </Button>
            ) : (
              <Button
                onClick={signInWithGoogle}
                size="lg"
                disabled={isLoading}
                className="w-full sm:w-auto h-11 px-5 text-sm font-medium bg-white hover:bg-stone-50 text-stone-900 border border-stone-200 hover:border-stone-300 shadow-sm hover:shadow-md hover:-translate-y-px active:translate-y-0 transition-all duration-200 rounded-full dark:bg-stone-900 dark:hover:bg-stone-800 dark:text-stone-100 dark:border-stone-700 dark:hover:border-stone-600"
              >
                {isLoading ? (
                  <span className="flex items-center gap-2">
                    <svg
                      className="animate-spin h-5 w-5"
                      viewBox="0 0 24 24"
                      aria-hidden
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                        fill="none"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V8a4 4 0 100 8v4a8 8 0 01-8-8z"
                      />
                    </svg>
                    Signing in…
                  </span>
                ) : (
                  <span className="flex items-center gap-3">
                    <img
                      src="/google.svg"
                      alt=""
                      className="h-4 w-4"
                    />
                    Sign in with Google
                  </span>
                )}
              </Button>
            )}

            {!user && (
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-500/10 text-amber-700 dark:text-amber-300 border border-amber-500/25 font-semibold">
                  <ChefCoinIcon className="h-3.5 w-3.5" />
                  +100 ChefCoins
                </span>
                <span>free for new creators</span>
              </div>
            )}
          </div>

          {/* Pillars */}
          <div className="grid gap-2.5 md:gap-4 mb-8 md:mb-auto">
            {PILLARS.map((p) => {
              const Icon = p.icon;
              return (
                <div
                  key={p.title}
                  className="flex gap-3 items-start p-3 md:p-3.5 rounded-xl bg-white/60 dark:bg-stone-900/40 border border-stone-200/60 dark:border-stone-800/60 hover:border-terracotta-300/60 dark:hover:border-terracotta-700/60 hover:shadow-sm transition-all"
                >
                  <div
                    className={`p-1.5 md:p-2 rounded-lg ${p.bg} shrink-0`}
                  >
                    <Icon className={`h-4 w-4 md:h-5 md:w-5 ${p.accent}`} />
                  </div>
                  <div className="min-w-0">
                    <h3 className="font-semibold text-foreground text-[13px] md:text-sm mb-0.5">
                      {p.title}
                    </h3>
                    <p className="text-[11.5px] md:text-sm text-muted-foreground leading-snug md:leading-relaxed">
                      {p.description}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Footer */}
          <footer className="hidden md:block pt-8 mt-8 text-xs text-muted-foreground">
            © {new Date().getFullYear()} Cheflab · All rights reserved
          </footer>
        </div>

        {/* ============== RIGHT: Recipe mosaic (mobile-first hero+grid, desktop mosaic) ============== */}
        <div className="order-1 md:order-2 w-full md:w-7/12 lg:w-3/5 relative overflow-hidden bg-stone-950 min-h-[480px]">
          {/* Subtle warm gradient base — replaces the old chefs image background */}
          <div
            className="absolute inset-0 bg-gradient-to-br from-stone-950 via-stone-900 to-[#3a1a0d]"
            aria-hidden
          />
          {/* Soft glow accents */}
          <div
            className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-terracotta-500/25 blur-3xl"
            aria-hidden
          />
          <div
            className="absolute -bottom-40 -left-20 w-96 h-96 rounded-full bg-amber-500/20 blur-3xl"
            aria-hidden
          />
          {/* Faint texture grid for depth */}
          <div
            className="absolute inset-0 opacity-[0.04] mix-blend-overlay"
            style={{
              backgroundImage:
                'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
              backgroundSize: '24px 24px',
            }}
            aria-hidden
          />

          {/* Mobile: 1 hero card (the "Meet the Chefs" feature image
              that desktop uses) + 2-col grid of live studio recipes.
              All cards share a uniform 4/3 aspect on mobile. */}
          <div className="md:hidden relative z-10 px-4 pt-6 pb-6">
            <div className="flex items-center gap-2 text-white/90 text-[11px] font-semibold uppercase tracking-[0.14em] mb-3">
              <span className="inline-flex items-center justify-center h-6 w-6 rounded-md bg-white/10 backdrop-blur-sm">
                <ChefHat className="h-3 w-3" />
              </span>
              <span>Made in Studio</span>
            </div>

            {/* Hero: the desktop feature card (kitchen image) — preserved
                untouched on mobile as the visual anchor. */}
            <MosaicCard
              card={liveCards[0]}
              imgError={!!imgErrors[liveCards[0].id]}
              onImgError={() => handleImgError(liveCards[0].id)}
              className="w-full mb-3"
            />

            {/* 2-col grid of the first 4 live recipes */}
            <div className="grid grid-cols-2 gap-2.5">
              {liveCards.slice(1, 5).map((card) => (
                <MosaicCard
                  key={card.id}
                  card={card}
                  imgError={!!imgErrors[card.id]}
                  onImgError={() => handleImgError(card.id)}
                  className="w-full"
                />
              ))}
            </div>
          </div>

          {/* Desktop: grid mosaic */}
          <div className="hidden md:flex relative z-10 h-full items-center justify-center p-8 lg:p-12">
            <div className="w-full max-w-2xl">
              <div className="flex items-center gap-2 text-white/90 text-xs font-semibold uppercase tracking-wider mb-5">
                <span className="inline-flex items-center justify-center h-7 w-7 rounded-lg bg-white/15 backdrop-blur-sm">
                  <ChefHat className="h-3.5 w-3.5" />
                </span>
                <span>Recipes made in Studio</span>
              </div>
              <div className="grid grid-cols-3 grid-rows-3 gap-3 lg:gap-4">
                {liveCards.map((card) => (
                  <div
                    key={card.id}
                    className={`${card.span} ${card.rotate} ${card.translate} transition-transform duration-500 hover:rotate-0 hover:scale-[1.02] hover:z-10`}
                  >
                    <MosaicCard
                      card={card}
                      imgError={!!imgErrors[card.id]}
                      onImgError={() => handleImgError(card.id)}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Mobile footer */}
          <footer className="md:hidden relative z-10 text-center text-xs text-white/70 pb-4 px-6">
            © {new Date().getFullYear()} Cheflab · All rights reserved
          </footer>
        </div>
      </div>
    </div>
  );
};

const MosaicCard: React.FC<{
  card: typeof MOSAIC_CARDS[number] & {
    recipeId?: number;
    chefAvatar?: string;
    chefName?: string;
  };
  imgError: boolean;
  onImgError: () => void;
  className?: string;
}> = ({ card, imgError, onImgError, className = '' }) => {
  const hasLiveImage = card.id !== 'feature' && Boolean(card.image);
  const href =
    card.id !== 'feature' && card.recipeId
      ? `https://www.cheflab.xyz/recipe/${card.recipeId}`
      : null;

  const inner = (
    <div
      className={`relative overflow-hidden rounded-2xl bg-stone-800 shadow-2xl ring-1 ring-white/10 ${
        href ? 'cursor-pointer' : ''
      } ${className}`}
    >
      <div
        className={`relative ${
          card.id === 'feature' ? 'aspect-[16/9] md:aspect-square' : 'aspect-[4/3]'
        }`}
      >
        {!imgError && (card.id === 'feature' || hasLiveImage) ? (
          <img
            src={card.image}
            alt={card.title}
            className={`absolute inset-0 w-full h-full object-cover ${
              card.id === 'feature' ? 'object-[center_30%]' : ''
            }`}
            loading="lazy"
            onError={onImgError}
          />
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-terracotta-500 to-amber-600 flex items-center justify-center text-white">
            <ChefHat className="h-10 w-10" />
          </div>
        )}

        {/* Top tag — only for the feature card. Small cards show the
            chef avatar in the bottom-right instead. */}
        {card.id === 'feature' && (
          <div className="absolute top-2.5 left-2.5 z-10">
            <span
              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold backdrop-blur-sm ${
                card.tag === 'Studio'
                  ? 'bg-terracotta-500/95 text-white'
                  : 'bg-white/90 text-stone-700'
              }`}
            >
              {card.tag === 'Studio' && <Sparkles className="h-2.5 w-2.5" />}
              {card.tag}
            </span>
          </div>
        )}

        {/* Chef avatar — small cards only.
            Subtle warm-terracotta ring + soft drop shadow, sitting above
            the bottom gradient. No stark white border — keeps with the
            dark/terracotta palette of the right column. */}
        {card.id !== 'feature' && card.chefAvatar && (
          <div
            className="absolute bottom-9 right-2 z-20"
            title={card.chefName ? `Created by ${card.chefName}` : undefined}
          >
            <div className="h-8 w-8 sm:h-9 sm:w-9 rounded-full bg-stone-900/60 ring-1 ring-terracotta-400/40 shadow-[0_2px_8px_rgba(0,0,0,0.5)] overflow-hidden flex items-center justify-center">
              <img
                src={card.chefAvatar}
                alt={card.chefName || 'Chef'}
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
          </div>
        )}

        {/* Bottom gradient + meta */}
        <div className="absolute inset-x-0 bottom-0 z-10 bg-gradient-to-t from-black/80 via-black/40 to-transparent pt-8 pb-2.5 px-3 text-white">
          <h3
            className={`font-semibold leading-tight drop-shadow ${
              card.id === 'feature' ? 'text-base md:text-lg' : 'text-sm'
            }`}
          >
            {card.title}
          </h3>
          {card.id === 'feature' ? (
            <div className="flex items-center gap-2.5 text-[10px] text-white/85 mt-1">
              {card.minutes !== null && (
                <span className="inline-flex items-center gap-0.5">
                  <Clock className="h-2.5 w-2.5" />
                  {card.minutes}m
                </span>
              )}
              {card.rating !== null && (
                <span className="inline-flex items-center gap-0.5">
                  <Star className="h-2.5 w-2.5 fill-amber-300 text-amber-300" />
                  {card.rating}
                </span>
              )}
              <span className="opacity-80 truncate">· {card.meta}</span>
            </div>
          ) : (
            <div className="flex items-center gap-1.5 text-[10px] text-white/85 mt-1 truncate pr-10">
              {card.chefName && (
                <span className="truncate font-medium">{card.chefName}</span>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  if (href) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="block focus:outline-none focus-visible:ring-2 focus-visible:ring-terracotta-400 focus-visible:ring-offset-2 focus-visible:ring-offset-stone-950 rounded-2xl"
      >
        {inner}
      </a>
    );
  }

  return inner;
};

export default LoginPage;
